/**
 * Language Context for EzERP
 * Provides language state and switching functionality across the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from '@/i18n';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [isChanging, setIsChanging] = useState(false);

  // Sync with i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
      document.documentElement.lang = lng;
      document.documentElement.dir = lng === 'ar' || lng === 'he' ? 'rtl' : 'ltr';
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Get token from localStorage for authenticated requests
  const getAuthToken = useCallback(() => {
    try {
      const storedAuth = localStorage.getItem('ezerpToken');
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        return parsed.token;
      }
    } catch (e) {
      return null;
    }
    return null;
  }, []);

  // Change language with persistence
  const setLanguage = useCallback(async (languageCode) => {
    if (languageCode === currentLanguage || isChanging) return;
    
    setIsChanging(true);
    try {
      const token = getAuthToken();
      await changeLanguage(languageCode, token);
      setCurrentLanguage(languageCode);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsChanging(false);
    }
  }, [currentLanguage, isChanging, getAuthToken]);

  // Get language info
  const getLanguageInfo = useCallback((code = currentLanguage) => {
    return SUPPORTED_LANGUAGES.find(lang => lang.code === code) || SUPPORTED_LANGUAGES[0];
  }, [currentLanguage]);

  const value = {
    currentLanguage,
    setLanguage,
    isChanging,
    supportedLanguages: SUPPORTED_LANGUAGES,
    getLanguageInfo,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
